import React from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import type { Competition } from '@shared';
import { COMPETITION_TYPE_LABELS_UPPER as TYPE_LABELS } from '@shared';
import { ArrowRight, Calendar, Check, ChevronRight, CreditCard, Euro, LogIn, MapPin, Users } from 'lucide-react';

interface RegistrationSidebarProps {
  competition: Competition;
  price: number;
  total: number;
  isFree: boolean;
  placesLeft: number | null;
  isLoggedIn: boolean;
  loginUrl: string;
  isPending: boolean;
  onSubmit: (e: React.FormEvent) => void;
  /** Si true, le bouton submit est désactivé (licences non valides) */
  licensesBlocking?: boolean;
  /** Si true, le bouton submit est désactivé (nom d'équipe déjà pris sur le concours) */
  teamNameBlocking?: boolean;
}

export default function RegistrationSidebar({
  competition,
  price,
  total,
  isFree,
  placesLeft,
  isLoggedIn,
  loginUrl,
  isPending,
  onSubmit,
  licensesBlocking = false,
  teamNameBlocking = false,
}: RegistrationSidebarProps) {
  return (
    <div className='lg:w-80 shrink-0'>
      <div className='sticky top-32 space-y-6'>
        {/* Détails concours */}
        <div className='bg-slate-50 rounded-3xl border border-slate-100 p-6'>
          <h3 className='font-bold text-sm text-slate-900 mb-3'>Détails du concours</h3>
          <div className='space-y-2 text-sm text-slate-600'>
            <div className='flex items-center gap-2'>
              <Users className='w-4 h-4 text-blue-500 shrink-0' />
              <span>
                {competition.type ? TYPE_LABELS[competition.type] : '—'} — {competition.playersPerTeam ?? '?'}{' '}
                joueur(s)/équipe
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <Calendar className='w-4 h-4 text-blue-500 shrink-0' />
              <span>
                {competition.date
                  ? new Date(competition.date).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })
                  : '—'}
              </span>
            </div>
            {(competition.venue ?? competition.city) && (
              <div className='flex items-center gap-2'>
                <MapPin className='w-4 h-4 text-blue-500 shrink-0' />
                <span>{competition.venue ?? competition.city}</span>
              </div>
            )}
            {competition.maxTeams && (
              <div className='flex items-center gap-2'>
                <Euro className='w-4 h-4 text-blue-500 shrink-0' />
                <span>
                  {competition.currentTeams}/{competition.maxTeams} équipes inscrites
                  {placesLeft !== null && ` (${placesLeft} places)`}
                </span>
              </div>
            )}
          </div>
          <Link
            to={ROUTES.CONCOUR.replace(':id', String(competition.id))}
            className='mt-4 text-sm text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1'
          >
            Voir la page du concours <ChevronRight className='w-4 h-4' />
          </Link>
        </div>

        {/* Récapitulatif paiement */}
        <div className='bg-white rounded-3xl border border-slate-100 shadow-sm p-6'>
          <h3 className='font-bold text-lg text-slate-900 mb-4'>Récapitulatif</h3>
          <div className='space-y-3 mb-6'>
            <div className='flex items-center justify-between text-sm'>
              <span className='text-slate-600'>Droit d&apos;inscription</span>
              <span className='font-semibold text-slate-900'>
                {price > 0 ? `${(price / 100).toFixed(2)} €` : 'Gratuit'}
              </span>
            </div>
            <div className='border-t border-slate-100 pt-3 flex items-center justify-between'>
              <span className='font-bold text-slate-900'>Total</span>
              <span className='text-2xl font-extrabold text-blue-600'>
                {isFree ? 'Gratuit' : `${(total / 100).toFixed(2)} €`}
              </span>
            </div>
          </div>

          {!isLoggedIn ? (
            <Link
              to={loginUrl}
              className='flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 py-4 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-700'
            >
              <LogIn className='h-5 w-5' />
              Connexion pour s&apos;inscrire
            </Link>
          ) : (
            <button
              type='button'
              onClick={e => onSubmit(e as unknown as React.FormEvent)}
              disabled={isPending || licensesBlocking || teamNameBlocking}
              className='w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-4 rounded-2xl font-bold text-sm shadow-lg shadow-blue-600/25 transition-all flex items-center justify-center gap-2'
            >
              {isPending ? (
                'Inscription en cours...'
              ) : (
                <>
                  {isFree ? (
                    <>
                      <Check className='w-5 h-5' /> Confirmer l&apos;inscription
                    </>
                  ) : (
                    <>
                      <CreditCard className='w-5 h-5' /> Payer et s&apos;inscrire
                      <ArrowRight className='w-4 h-4' />
                    </>
                  )}
                </>
              )}
            </button>
          )}

          {licensesBlocking && isLoggedIn && (
            <p className='text-xs text-red-500 text-center mt-3 font-medium'>
              Toutes les licences doivent être valides pour la saison en cours avant de pouvoir vous inscrire.
            </p>
          )}

          {teamNameBlocking && !licensesBlocking && isLoggedIn && (
            <p className='text-xs text-red-500 text-center mt-3 font-medium'>
              Ce nom d&apos;équipe est déjà utilisé pour ce concours — choisissez-en un autre.
            </p>
          )}

          {!isFree && isLoggedIn && !licensesBlocking && (
            <p className='text-xs text-slate-400 text-center mt-3'>
              Paiement sécurisé par Stripe — ne quittez pas la page pendant le paiement.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
